import React, { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { FloppyDisk, ImageSquare, CloudArrowUp, Cigarette, Plus, X, WhatsappLogo, ArrowsClockwise, EnvelopeSimple, PaperPlaneTilt, ShieldCheck } from "@phosphor-icons/react";

const DEFAULT_CITISINA = {
  enabled: true,
  title: "Protocollo Citisina (Tabex)",
  description: "Supporto alla sospensione del fumo per pazienti odontoiatrici post-intervento.",
  duration_days: 25,
  schema_steps: [
    { day_range: "Giorni 1-3", dose: "1 compressa ogni 2 ore (6/die)", notes: "Riduci a max 5 sigarette/die" },
  ],
};

const FOLLOWUP_DAYS = [0, 1, 3, 7, 14, 30];
const FOLLOWUP_LABELS = {
  0: { title: "Giorno 0 — Invio iniziale", hint: "Inviato subito dopo la creazione del paziente. Spiega cosa troverà nel portale." },
  1: { title: "Giorno 1 — Primo controllo", hint: "Il giorno successivo all'intervento, primo check post-operatorio." },
  3: { title: "Giorno 3 — Verifica decorso", hint: "Controllo del decorso al 3° giorno (con richiesta foto)." },
  7: { title: "Giorno 7 — Una settimana", hint: "Controllo a una settimana dall'intervento." },
  14: { title: "Giorno 14 — Monitoraggio continuo", hint: "Aggiornamento sulla guarigione a 2 settimane." },
  30: { title: "Giorno 30 — Controllo finale", hint: "Ultimo follow-up del decorso, a un mese." },
};

const DEFAULT_FOLLOWUP_TEMPLATES = {
  "0": "Buongiorno Sig./Sig.ra {patient_name},\n\nla contattiamo dallo Studio {clinic_name} del Dr. {doctor_name}.\n\nPer seguirLa nel migliore dei modi dopo l'intervento di *{procedure}*, Le inviamo il Suo accesso personale al Portale Paziente.\n\nAttraverso questo portale potremo monitorare insieme il decorso post-operatorio, verificare il Suo stato di salute e intercettare tempestivamente eventuali problemi.\n\nLa invitiamo ad aprire il seguente link:\n{portal_link}\n\nPIN personale: {pin}\n\nLa compilazione richiede meno di due minuti ed è molto importante per consentirci di seguirLa con la massima attenzione.\n\nRestiamo naturalmente a Sua disposizione per qualsiasi necessità.\n\nStudio {clinic_name}",
  "1": "Buongiorno Sig./Sig.ra {patient_name},\n\ncome concordato desideriamo sapere come sta trascorrendo la prima giornata dopo l'intervento.\n\nLe chiediamo cortesemente di compilare il breve controllo clinico accedendo al Suo Portale Paziente.\n\n{portal_link}\n\nPIN: {pin}\n\nLe Sue risposte saranno analizzate dal nostro sistema di follow-up e, qualora emergessero elementi che meritano attenzione, il nostro studio verrà immediatamente avvisato.\n\nGrazie per la collaborazione.\n\nStudio {clinic_name}",
  "3": "Buongiorno Sig./Sig.ra {patient_name},\n\nsiamo al terzo giorno dal Suo intervento di {procedure}.\n\nÈ il momento ideale per verificare che la guarigione stia procedendo regolarmente.\n\nAcceda al Suo Portale Paziente:\n{portal_link}\n\nPIN: {pin}\n\nIl controllo richiede pochi minuti ma ci permette di seguirLa in maniera molto più precisa.\n\nStudio {clinic_name}",
  "7": "Buongiorno Sig./Sig.ra {patient_name},\n\nè trascorsa circa una settimana dal Suo intervento.\n\nVorremmo verificare insieme l'andamento della guarigione.\n\nAcceda al Suo Portale Paziente:\n{portal_link}\n\nPIN: {pin}\n\nQualora fosse necessario, potremo contattarLa rapidamente per programmare un controllo.\n\nStudio {clinic_name}",
  "14": "Buongiorno Sig./Sig.ra {patient_name},\n\ncontinuiamo il monitoraggio della guarigione.\n\nLa invitiamo a compilare il breve questionario disponibile nel Suo Portale Paziente.\n\n{portal_link}\n\nPIN: {pin}\n\nLa Sua collaborazione ci consente di seguirLa con maggiore precisione.\n\nStudio {clinic_name}",
  "30": "Buongiorno Sig./Sig.ra {patient_name},\n\nè trascorso circa un mese dal Suo intervento di {procedure}.\n\nVorremmo effettuare l'ultimo controllo del decorso post-operatorio.\n\nAcceda al Portale Paziente:\n{portal_link}\n\nPIN: {pin}\n\nGrazie per la fiducia accordata al nostro studio.\n\nStudio {clinic_name}",
};

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const fileInputRef = useRef(null);
  const [logoBust, setLogoBust] = useState(Date.now());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data || {});
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { ...settings };
      delete payload._id;
      delete payload.id;
      delete payload.updated_at;
      delete payload.logo_path;
      delete payload.logo_file_id;
      await api.patch("/settings", payload);
      setSavedAt(new Date().toLocaleTimeString("it-IT"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/settings");
      setSettings(data || {});
      setLogoBust(Date.now());
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!settings) return <div className="text-center text-[#56635D] py-12">Caricamento…</div>;

  const logoUrl = settings.logo_path ? `${API}/settings/logo?t=${logoBust}` : null;

  return (
    <div className="max-w-3xl space-y-6 fade-up" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Impostazioni</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#1C2321] mt-2">Studio & Comunicazioni</h1>
      </div>

      <div className="elev-card p-6 space-y-5">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Identità studio</div>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="w-32 h-32 rounded-xl border border-[#E6E8E6] bg-[#F7F7F5] flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo Mediadent" className="w-full h-full object-contain" data-testid="settings-logo" />
            ) : (
              <ImageSquare size={36} weight="duotone" className="text-brand-300" />
            )}
          </div>
          <div className="flex-1">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
            <button onClick={() => fileInputRef.current?.click()} data-testid="upload-logo-button" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-4 py-2.5 rounded-lg text-sm">
              <CloudArrowUp size={16} weight="duotone" /> Carica logo
            </button>
            <p className="text-xs text-[#56635D] mt-3">PNG/JPG/SVG. Verrà mostrato nel portale paziente e nei PDF.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SField label="Nome studio"><input value={settings.clinic_name || ""} onChange={(e) => update("clinic_name", e.target.value)} data-testid="settings-clinic-name" className="input" /></SField>
          <SField label="Nome del dottore"><input value={settings.doctor_name || ""} onChange={(e) => update("doctor_name", e.target.value)} placeholder="es. Mario Rossi" data-testid="settings-doctor-name" className="input" /></SField>
          <SField label="Telefono"><input value={settings.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="settings-phone" className="input" /></SField>
          <SField label="Email"><input type="email" value={settings.email || ""} onChange={(e) => update("email", e.target.value)} data-testid="settings-email" className="input" /></SField>
          <SField label="Indirizzo" full><input value={settings.address || ""} onChange={(e) => update("address", e.target.value)} data-testid="settings-address" className="input" /></SField>
          <SField label="URL pubblico portale" full>
            <input value={settings.public_base_url || ""} onChange={(e) => update("public_base_url", e.target.value)} placeholder="https://care.mediadentsarzana.it" data-testid="settings-public-url" className="input" />
          </SField>
        </div>
      </div>

      <FollowupTemplatesEditor settings={settings} update={update} />

      <EmailSettingsEditor />

      <CitisinaEditor settings={settings} update={update} />

      {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}
      {savedAt && <div className="text-sm text-brand-700">Salvato alle {savedAt}</div>}

      <button onClick={save} disabled={saving} data-testid="settings-save-button" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg disabled:opacity-60">
        <FloppyDisk size={18} weight="bold" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
      </button>

      <style>{`.input{width:100%;padding:.65rem .85rem;border:1px solid #E6E8E6;border-radius:.5rem;background:#fff;transition:border-color .2s} .input:focus{border-color:#2C4A3B;outline:none}`}</style>
    </div>
  );
}

function SField({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function CitisinaEditor({ settings, update }) {
  const protocol = settings.citisina_protocol || DEFAULT_CITISINA;
  const steps = protocol.schema_steps || [];

  const setProtocol = (next) => update("citisina_protocol", next);
  const setField = (k, v) => setProtocol({ ...protocol, [k]: v });
  const setStep = (i, k, v) => {
    const arr = [...steps];
    arr[i] = { ...arr[i], [k]: v };
    setProtocol({ ...protocol, schema_steps: arr });
  };
  const addStep = () => setProtocol({ ...protocol, schema_steps: [...steps, { day_range: "", dose: "", notes: "" }] });
  const removeStep = (i) => {
    const arr = [...steps];
    arr.splice(i, 1);
    setProtocol({ ...protocol, schema_steps: arr });
  };
  const resetDefault = () => setProtocol(DEFAULT_CITISINA);

  return (
    <div className="elev-card p-6 space-y-4" data-testid="citisina-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-400 flex items-center gap-1.5">
            <Cigarette size={12} weight="duotone" /> Protocollo Citisina
          </div>
          <h3 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Supporto sospensione fumo</h3>
          <p className="text-xs text-[#56635D] mt-1">Mostrato ai pazienti fumatori al Giorno 0 e nei follow-up successivi.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!protocol.enabled} onChange={(e) => setField("enabled", e.target.checked)} data-testid="citisina-enabled" className="accent-brand-700" />
          <span>Attivo</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SField label="Titolo">
          <input value={protocol.title || ""} onChange={(e) => setField("title", e.target.value)} data-testid="citisina-title" className="input" />
        </SField>
        <SField label="Durata (giorni)">
          <input type="number" min="1" max="60" value={protocol.duration_days || 25} onChange={(e) => setField("duration_days", parseInt(e.target.value, 10) || 25)} data-testid="citisina-duration" className="input" />
        </SField>
        <SField label="Descrizione" full>
          <textarea rows={2} value={protocol.description || ""} onChange={(e) => setField("description", e.target.value)} data-testid="citisina-description" className="input" />
        </SField>
      </div>

      <div className="pt-2">
        <div className="text-xs uppercase tracking-[0.18em] text-brand-400 mb-3">Schema di somministrazione</div>
        <div className="space-y-2">
          {steps.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 border border-[#E6E8E6] rounded-lg bg-white" data-testid={`citisina-step-${i}`}>
              <input value={s.day_range || ""} onChange={(e) => setStep(i, "day_range", e.target.value)} placeholder="Giorni 1-3" className="input md:col-span-3" data-testid={`citisina-step-${i}-range`} />
              <input value={s.dose || ""} onChange={(e) => setStep(i, "dose", e.target.value)} placeholder="1 cpr ogni 2 ore" className="input md:col-span-4" data-testid={`citisina-step-${i}-dose`} />
              <input value={s.notes || ""} onChange={(e) => setStep(i, "notes", e.target.value)} placeholder="Note opzionali" className="input md:col-span-4" data-testid={`citisina-step-${i}-notes`} />
              <button type="button" onClick={() => removeStep(i)} data-testid={`citisina-step-${i}-remove`} className="md:col-span-1 inline-flex items-center justify-center text-[#A84A4A] hover:text-red-700 rounded-md">
                <X size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={addStep} data-testid="citisina-add-step" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-3 py-2 rounded-lg text-sm">
            <Plus size={14} weight="bold" /> Aggiungi step
          </button>
          <button type="button" onClick={resetDefault} data-testid="citisina-reset" className="inline-flex items-center gap-2 bg-white border border-[#E6E8E6] text-[#56635D] hover:border-brand-300 px-3 py-2 rounded-lg text-sm">
            Ripristina default
          </button>
        </div>
      </div>
    </div>
  );
}

function FollowupTemplatesEditor({ settings, update }) {
  const templates = settings.followup_templates || {};

  const setTemplate = (day, text) => {
    update("followup_templates", { ...templates, [String(day)]: text });
  };
  const resetOne = (day) => {
    update("followup_templates", { ...templates, [String(day)]: DEFAULT_FOLLOWUP_TEMPLATES[String(day)] || "" });
  };
  const resetAll = () => update("followup_templates", { ...DEFAULT_FOLLOWUP_TEMPLATES });

  const VAR_CHIPS = [
    { k: "{patient_name}", h: "Nome paziente" },
    { k: "{clinic_name}", h: "Nome studio" },
    { k: "{doctor_name}", h: "Dottore" },
    { k: "{procedure}", h: "Tipo intervento" },
    { k: "{portal_link}", h: "Link portale" },
    { k: "{pin}", h: "PIN paziente" },
    { k: "{followup_day}", h: "Giorno follow-up" },
  ];

  return (
    <div className="elev-card p-6 space-y-4" data-testid="followup-templates-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-400 flex items-center gap-1.5">
            <WhatsappLogo size={12} weight="duotone" /> Template Messaggi WhatsApp
          </div>
          <h3 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Comunicazioni automatiche per i follow-up</h3>
          <p className="text-xs text-[#56635D] mt-1">Personalizza i messaggi inviati al paziente in ogni fase. Il sistema seleziona automaticamente il template in base al giorno post-operatorio.</p>
        </div>
        <button type="button" onClick={resetAll} data-testid="templates-reset-all" className="inline-flex items-center gap-1.5 bg-white border border-[#E6E8E6] text-[#56635D] hover:border-brand-300 px-3 py-2 rounded-lg text-xs">
          <ArrowsClockwise size={12} weight="bold" /> Ripristina tutti i default
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#E6E8E6]">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#56635D] w-full mb-1">Variabili disponibili (clicca per copiare)</span>
        {VAR_CHIPS.map((v) => (
          <button
            key={v.k}
            type="button"
            onClick={() => { try { navigator.clipboard.writeText(v.k); } catch { /* noop */ } }}
            title={v.h}
            data-testid={`var-chip-${v.k.replace(/[{}]/g, "")}`}
            className="font-mono text-[11px] bg-[#F0F0EE] hover:bg-brand-50 text-brand-700 px-2 py-1 rounded border border-[#E6E8E6] transition-colors"
          >
            {v.k}
          </button>
        ))}
      </div>

      <div className="space-y-3 pt-2">
        {FOLLOWUP_DAYS.map((day) => {
          const meta = FOLLOWUP_LABELS[day];
          const value = templates[String(day)] ?? "";
          return (
            <details key={day} className="border border-[#E6E8E6] rounded-lg bg-white" data-testid={`template-day-${day}`}>
              <summary className="cursor-pointer px-4 py-3 flex items-center justify-between hover:bg-[#F7F7F5] rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-brand-700 text-white flex items-center justify-center text-sm font-mono shrink-0">{day}</span>
                  <div>
                    <div className="text-sm font-medium text-[#1C2321]">{meta.title}</div>
                    <div className="text-xs text-[#56635D]">{meta.hint}</div>
                  </div>
                </div>
                <span className="text-xs text-brand-700 font-mono shrink-0">{value ? `${value.length} car.` : "vuoto"}</span>
              </summary>
              <div className="px-4 pb-4 space-y-2">
                <textarea
                  rows={10}
                  value={value}
                  onChange={(e) => setTemplate(day, e.target.value)}
                  data-testid={`template-day-${day}-textarea`}
                  className="w-full px-3 py-2.5 border border-[#E6E8E6] rounded-lg bg-white font-mono text-[13px] leading-relaxed focus:border-brand-700"
                />
                <button type="button" onClick={() => resetOne(day)} data-testid={`template-day-${day}-reset`} className="inline-flex items-center gap-1.5 text-xs text-[#56635D] hover:text-brand-700">
                  <ArrowsClockwise size={11} weight="bold" /> Ripristina testo di default per il Giorno {day}
                </button>
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}



const EMAIL_DAY_LABELS = {
  "0": "Giorno 0 — Invio iniziale",
  "1": "Giorno 1 — Primo controllo",
  "3": "Giorno 3 — Verifica decorso",
  "7": "Giorno 7 — Una settimana",
  "14": "Giorno 14 — Monitoraggio",
  "30": "Giorno 30 — Controllo finale",
};

function EmailSettingsEditor() {
  const [data, setData] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testStatus, setTestStatus] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/settings/email`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Errore caricamento config email");
    }
  };
  useEffect(() => { load(); }, []);

  const saveTemplates = async (newTpls) => {
    try {
      const { data } = await api.patch(`/settings/email/templates`, newTpls);
      setData((d) => ({ ...d, templates: data.templates }));
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Errore template email");
    }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { setTestStatus({ ok: false, error: "Inserisci un'email valida" }); return; }
    setTestStatus({ pending: true });
    try {
      const { data } = await api.post(`/settings/email/test`, { to_email: testTo });
      setTestStatus(data);
    } catch (e) {
      setTestStatus({ ok: false, error: formatApiError(e?.response?.data?.detail) || "Errore" });
    }
  };

  if (!data) return null;
  const env = data.env || {};

  return (
    <div className="elev-card p-6 space-y-5" data-testid="email-settings-editor">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 flex items-center gap-1.5">
          <EnvelopeSimple size={12} weight="duotone" /> Configurazione Email
        </div>
        <h3 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Canale email per i follow-up</h3>
        <p className="text-xs text-[#56635D] mt-1">Le credenziali SMTP sono caricate <strong>solo dalle variabili d'ambiente</strong> (Secrets). La password non viene mai salvata nel database né esposta al frontend.</p>
      </div>

      <div className={`rounded-lg p-4 border ${env.configured ? "bg-[#EAFBEF] border-[#1F8B4C]/30" : "bg-[#FAEBEB] border-[#A84A4A]/30"}`} data-testid="email-env-status">
        <div className="flex items-center gap-2 mb-2">
          {env.configured ? <ShieldCheck size={18} weight="fill" className="text-[#1F8B4C]" /> : <ShieldCheck size={18} weight="duotone" className="text-[#A84A4A]" />}
          <strong className={`text-sm ${env.configured ? "text-[#1F8B4C]" : "text-[#A84A4A]"}`}>{env.configured ? "SMTP configurato" : "SMTP non configurato"}</strong>
        </div>
        {env.configured ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#1C2321]">
            <div><span className="text-[10px] uppercase tracking-[0.18em] text-[#56635D] block">Host</span>{env.smtp_host}:{env.smtp_port}</div>
            <div><span className="text-[10px] uppercase tracking-[0.18em] text-[#56635D] block">Sicurezza</span>{(env.security || "").toUpperCase()}</div>
            <div><span className="text-[10px] uppercase tracking-[0.18em] text-[#56635D] block">From email</span>{env.from_email}</div>
            <div><span className="text-[10px] uppercase tracking-[0.18em] text-[#56635D] block">From name</span>{env.from_name}</div>
          </div>
        ) : (
          <div className="text-sm text-[#1C2321]">
            <p>Mancano le seguenti variabili d'ambiente:</p>
            <ul className="mt-2 space-y-1">
              {(env.missing || []).map((m) => (
                <li key={m} className="font-mono text-xs bg-white border border-[#A84A4A]/30 rounded px-2 py-1 inline-block mr-1">{m}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-[#56635D]">Imposta queste variabili nei <strong>Secrets di Emergent</strong> (Profile → Secrets) per il deployment di produzione. In preview, modifica <span className="font-mono">/app/backend/.env</span> e riavvia il backend.</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E6E8E6]">
        <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email destinatario test" data-testid="email-test-to" className="input" style={{ width: 260 }} />
        <button type="button" onClick={sendTest} disabled={!env.configured} data-testid="email-test-send" className="inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
          <PaperPlaneTilt size={14} weight="duotone" /> Invia email di test
        </button>
      </div>
      {testStatus && (
        testStatus.pending ? <div className="text-xs text-[#56635D]">Invio in corso…</div> :
        testStatus.ok ? <div className="text-sm text-[#1F8B4C] bg-[#EAFBEF] rounded-lg px-3 py-2" data-testid="email-test-ok">Email di test inviata correttamente.</div> :
        <div className="text-sm text-[#A84A4A] bg-[#FAEBEB] rounded-lg px-3 py-2" data-testid="email-test-fail">{testStatus.error}</div>
      )}
      {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

      <EmailTemplatesPanel templates={data.templates} onSave={saveTemplates} />
    </div>
  );
}

function EmailTemplatesPanel({ templates, onSave }) {
  const [subject, setSubject] = useState(templates?.subject || "");
  const [bodies, setBodies] = useState(templates?.bodies || {});
  useEffect(() => { setSubject(templates?.subject || ""); setBodies(templates?.bodies || {}); }, [templates]);

  const setBody = (d, v) => setBodies((b) => ({ ...b, [d]: v }));

  return (
    <div className="pt-4 border-t border-[#E6E8E6] space-y-3" data-testid="email-templates-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-400">Template Email</div>
          <p className="text-xs text-[#56635D] mt-1">Variabili: <span className="font-mono text-[11px]">{"{patient_name} {clinic_name} {doctor_name} {procedure} {portal_link} {pin} {followup_day}"}</span></p>
        </div>
        <button type="button" onClick={() => onSave({ subject, bodies })} data-testid="email-templates-save" className="inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white px-3 py-1.5 rounded-lg text-xs">
          <FloppyDisk size={12} weight="duotone" /> Salva template
        </button>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">Oggetto (comune a tutti i giorni)</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" className="input font-mono text-sm" />
      </div>
      <div className="space-y-2">
        {Object.entries(EMAIL_DAY_LABELS).map(([day, label]) => (
          <details key={day} className="border border-[#E6E8E6] rounded-lg bg-white" data-testid={`email-template-day-${day}`}>
            <summary className="cursor-pointer px-4 py-2.5 flex items-center gap-3 hover:bg-[#F7F7F5]">
              <span className="w-7 h-7 rounded-full bg-brand-700 text-white flex items-center justify-center text-xs font-mono shrink-0">{day}</span>
              <span className="text-sm font-medium text-[#1C2321]">{label}</span>
              <span className="ml-auto text-[10px] font-mono text-brand-700">{(bodies[day] || "").length} car.</span>
            </summary>
            <div className="px-4 pb-3">
              <textarea rows={9} value={bodies[day] || ""} onChange={(e) => setBody(day, e.target.value)} data-testid={`email-body-${day}`} className="w-full px-3 py-2 border border-[#E6E8E6] rounded-lg bg-white font-mono text-[13px] leading-relaxed focus:border-brand-700" />
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
